import { readJson, writeJson } from '@lib/storage'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { detectLocale, type Locale } from './locale'
import { MESSAGES, type MessageKey } from './messages'
import { type TranslationVars, translate } from './translate'

const STORAGE_KEY = 'veracruz.locale'

type I18nValue = {
	locale: Locale
	setLocale: (locale: Locale) => void
	t: (key: MessageKey, vars?: TranslationVars) => string
}

const I18nContext = createContext<I18nValue | null>(null)

export const I18nProvider = ({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) => {
	const [locale, setLocaleState] = useState<Locale>(
		() => initialLocale ?? detectLocale(readJson(STORAGE_KEY, null), globalThis.navigator?.languages ?? [])
	)

	const setLocale = useCallback((next: Locale) => {
		setLocaleState(next)
		writeJson(STORAGE_KEY, next)
	}, [])

	// El atributo lang del documento sigue al idioma (lectores de pantalla, guionado).
	useEffect(() => {
		document.documentElement.lang = locale
	}, [locale])

	const value = useMemo<I18nValue>(
		() => ({ locale, setLocale, t: (key, vars) => translate(MESSAGES[locale], key, vars) }),
		[locale, setLocale]
	)
	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = (): I18nValue => {
	const value = useContext(I18nContext)
	if (!value) throw new Error('useI18n debe usarse dentro de <I18nProvider>')
	return value
}
